<template>
    <section name="VideosSlider">
        <div ref="swiperContainer" class="swiper max-w-6xl mx-auto">
            <div class="swiper-wrapper pb-8">
                <div
                    v-for="(video, index) in videosArray"
                    :key="index"
                    class="swiper-slide"
                >
                    <div
                        class="mx-auto w-full bg-gardenia lg:rounded-3xl overflow-hidden"
                    >
                        <button
                            type="button"
                            class="relative block w-full h-0 pb-[56.25%] cursor-pointer group focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-indigo-500"
                            @click="openVideo(video)"
                        >
                            <img
                                :src="
                                    video.image ||
                                    getYoutubeThumbnail(video.url, 'max')
                                "
                                alt=""
                                class="absolute inset-0 w-full h-full object-cover"
                            />
                            <div
                                class="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition duration-200"
                            ></div>

                            <!-- Play Icon -->
                            <div
                                class="absolute inset-0 flex items-center justify-center pointer-events-none"
                            >
                                <svg
                                    class="w-14 h-14 sm:w-16 sm:h-16 text-white drop-shadow-lg"
                                    viewBox="0 0 60 60"
                                    aria-hidden="true"
                                >
                                    <path
                                        fill="currentColor"
                                        d="m45.563 29.174-22-15A1 1 0 0 0 22 15v30a.999.999 0 0 0 1.563.826l22-15a1 1 0 0 0 0-1.652zM24 43.107V16.893L43.225 30 24 43.107z"
                                    />
                                    <path
                                        fill="currentColor"
                                        d="M30 0C13.458 0 0 13.458 0 30s13.458 30 30 30 30-13.458 30-30S46.542 0 30 0zm0 58C14.561 58 2 45.439 2 30S14.561 2 30 2s28 12.561 28 28-12.561 28-28 28z"
                                    />
                                </svg>
                            </div>
                        </button>
                    </div>
                </div>
            </div>

            <div
                ref="pagination"
                class="swiper-pagination -bottom-0 left-0 right-0 mx-auto flex w-full flex-row justify-center gap-3"
            ></div>
        </div>

        <!-- Modal -->
        <Teleport to="body">
            <div
                v-if="activeVideo"
                class="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
                @click.self="closeModal"
            >
                <div class="w-full max-w-4xl aspect-video relative">
                    <iframe
                        class="absolute inset-0 w-full h-full rounded-lg"
                        :src="`https://www.youtube.com/embed/${getYoutubeId(activeVideo.url)}?autoplay=1`"
                        frameborder="0"
                        allow="autoplay; encrypted-media"
                        allowfullscreen
                    ></iframe>
                    <button
                        class="absolute top-2 right-2 text-white text-2xl"
                        @click="closeModal"
                    >
                        ✕
                    </button>
                </div>
            </div>
        </Teleport>
    </section>
</template>

<script setup lang="ts">
import {
    defineProps,
    ref,
    onMounted,
    onBeforeUnmount,
    computed,
    type Ref,
} from "vue";
import Swiper from "swiper";
import { Pagination, Autoplay } from "swiper/modules";
import "swiper/swiper-bundle.css";

interface VideoItem {
    title: string;
    subtitle: string;
    url: string;
    image?: string;
}

const props = defineProps<{ videos: Record<string, VideoItem> }>();

const videosArray = computed<VideoItem[]>(() => Object.values(props.videos));

const swiperContainer: Ref<HTMLElement | null> = ref(null);
const pagination: Ref<HTMLElement | null> = ref(null);

const activeVideo = ref<VideoItem | null>(null);

const getYoutubeId = (url: string): string => {
    try {
        const parsedUrl = new URL(url);
        if (parsedUrl.hostname.includes("youtu.be")) {
            return parsedUrl.pathname.slice(1);
        }
        if (parsedUrl.hostname.includes("youtube.com")) {
            return parsedUrl.searchParams.get("v") || "";
        }
    } catch {
        return "";
    }
    return "";
};

// const getYoutubeThumbnail = (url: string): string => {
//     const id = getYoutubeId(url);
//     return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : "";
// };

const getYoutubeThumbnail = (
    url: string,
    quality: "max" | "hq" | "sd" | "mq" = "max",
): string => {
    const id = getYoutubeId(url);
    if (!id) return "";

    const qualityMap: Record<typeof quality, string> = {
        max: "maxresdefault",
        hq: "hqdefault",
        sd: "sddefault",
        mq: "mqdefault",
    };

    return `https://img.youtube.com/vi/${id}/${qualityMap[quality]}.jpg`;
};

const openVideo = (video: VideoItem) => {
    activeVideo.value = video;
};

const closeModal = () => {
    activeVideo.value = null;
};

const handleEscape = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
        closeModal();
    }
};

onMounted(() => {
    window.addEventListener("keydown", handleEscape);

    if (swiperContainer.value) {
        // eslint-disable-next-line no-new
        new Swiper(swiperContainer.value, {
            modules: [Pagination, Autoplay],
            loop: true,
            autoplay: {
                delay: 5000,
                disableOnInteraction: false,
            },
            grabCursor: true,
            slidesPerView: 1,
            spaceBetween: 50,
            pagination: {
                el: pagination.value,
                clickable: true,
                renderBullet: () =>
                    '<span class="swiper-pagination-bullet main-slider-pagination-bullet w-12 h-4"></span>',
            },
        });
    }
});

onBeforeUnmount(() => {
    window.removeEventListener("keydown", handleEscape);
});
</script>
